import { Box, Tooltip, Typography } from "@mui/material"
import { extraColors } from "../../utils/theme"
import { V2_ACCENT, V2_FONT_MONO, V2_NUMERIC } from "./v2/biothreatV2Tokens"
import type { NowcastResult } from "./gdeltClusterAnalytics"

const EXPLAINER =
  "Projected total reports for today, since the day isn't finished yet. We " +
  "estimate today's rate from the recent daily trend and combine it with the " +
  "reports already counted. The trend matters more at the start of the day and " +
  "the live count matters more by the end. The projection is the reports so far " +
  "plus the expected rest of the day, and the band is the likely range."

/** Projected current-day reporting volume, accounting for backdated reporting. */
export function GdeltClusterNowcast({ nowcast }: { nowcast: NowcastResult }) {
  if (nowcast.insufficient) {
    return (
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          p: 1.5,
          borderRadius: "8px",
          border: `1px solid ${extraColors.translucent5}`,
        }}
      >
        <Eyebrow />
        <Typography
          variant="caption"
          sx={{ color: extraColors.hint, display: "block", mt: 0.5 }}
        >
          No current-day reporting to project.
        </Typography>
      </Box>
    )
  }

  return (
    <Tooltip arrow placement="top" title={EXPLAINER}>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          p: 1.5,
          borderRadius: "8px",
          border: `1px solid ${V2_ACCENT.cyanSoft}`,
          backgroundColor: "rgba(55,178,255,0.05)",
        }}
      >
        <Eyebrow />
        <Box
          sx={{ display: "flex", alignItems: "baseline", gap: 0.75, mt: 0.5 }}
        >
          <Typography
            sx={{ ...V2_NUMERIC, color: extraColors.white, fontSize: "1.5rem" }}
          >
            ~{nowcast.projectedToday}
          </Typography>
          <Typography
            sx={{
              fontFamily: V2_FONT_MONO,
              fontSize: "0.65rem",
              color: extraColors.translucent50,
            }}
          >
            reports today
          </Typography>
        </Box>
        <Typography
          sx={{
            fontFamily: V2_FONT_MONO,
            fontSize: "0.65rem",
            color: extraColors.translucent50,
            mt: 0.25,
          }}
        >
          {nowcast.observedToday} observed · range {nowcast.low}–{nowcast.high}
        </Typography>
      </Box>
    </Tooltip>
  )
}

function Eyebrow() {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Typography
        sx={{
          fontFamily: V2_FONT_MONO,
          fontSize: "0.6rem",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: extraColors.translucent50,
        }}
      >
        Projected today
      </Typography>
      <Box
        sx={{
          fontFamily: V2_FONT_MONO,
          fontSize: "0.5rem",
          letterSpacing: "0.08em",
          color: V2_ACCENT.gold,
          border: `1px solid ${V2_ACCENT.gold}`,
          borderRadius: "3px",
          px: 0.5,
          opacity: 0.9,
        }}
      >
        EST
      </Box>
    </Box>
  )
}
